// CustomAgGrid.tsx
// A reusable wrapper around AgGridReact that applies your custom theme.

import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import { type AgGridReact as AgGridReactType } from 'ag-grid-react/lib/agGridReact';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Import AG Grid and theme styles once, here in the wrapper
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

// Import the CSS module to apply your custom, scoped styles
import useAppTheme from 'hooks/useAppTheme';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import styles from './custom-ag-grid.module.css';

// We use a generic TData to pass down the row data type for full type safety.
// The props for our wrapper are the same as the original AgGridReactProps.
type CustomAgGridProps<TData = any> = AgGridReactProps<TData>;

/**
 * A themed wrapper around AgGridReact.
 * It automatically applies the light/dark theme and custom styles
 * via CSS modules, ensuring the theme is scoped only to this grid.
 * It accepts all props that AgGridReact accepts and forwards the ref.
 */
const CustomAgGrid = React.forwardRef<AgGridReactType<any>, CustomAgGridProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const { enableDarkTheme } = useAppTheme();
    const palette = enableDarkTheme
      ? clientThemePalette[CLIENT_THEME].dark
      : clientThemePalette[CLIENT_THEME].light;

    const defaultLocaleText = useMemo(
      () => ({
        page: t('ag_grid_page'),
        to: t('ag_grid_to'),
        of: t('ag_grid_of'),
        next: t('ag_grid_next'),
        last: t('ag_grid_last'),
        first: t('ag_grid_first'),
        previous: t('ag_grid_previous'),
        noRowsToShow: t('ag_grid_no_rows'),
        loadingOoo: t('ag_grid_loading'),
      }),
      [t]
    );

    // Determine the correct AG Grid theme class based on the app's theme
    const themeClass = useMemo(
      () => (enableDarkTheme ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'),
      [enableDarkTheme]
    );

    const cssVarOverrides: Record<string, string> = enableDarkTheme
      ? {
          '--header-bg': palette.toolBarBackground,
          '--row-bg-dark': palette.paperBackground,
          '--hover-bg-dark': `${palette.primary}1A`,
          '--selected-bg-dark': `${palette.primary}33`,
          '--selected-hover-bg-dark': `${palette.primary}33`,
          '--footer-bg-dark': palette.toolBarBackground,
          '--ag-background-color': palette.paperBackground,
        }
      : {
          '--header-bg': palette.primary,
        };

    return (
      <div
        className={styles.workOrderGridWrapper}
        style={{ height: '100%', width: '100%' }}
      >
        {/* cssVarOverrides applied here so they override the CSS module's variable declarations on this same element */}
        <div
          className={themeClass}
          style={{ height: '100%', width: '100%', ...cssVarOverrides }}
        >
          <AgGridReact localeText={defaultLocaleText} {...props} ref={ref} />
        </div>
      </div>
    );
  }
);

// Set a display name for the component for better React DevTools debugging
CustomAgGrid.displayName = 'CustomAgGrid';

export default CustomAgGrid;
